import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { Home, MessageCircle, Plus, Users, Bell, X } from 'lucide-react';
import { FEATURES } from '../../config/features.js';

const tabs = [
  { id: 'feed', path: '/', icon: Home, always: true },
  { id: 'chat', path: '/chats', icon: MessageCircle, feature: 'chat' },
  { id: 'plus', path: null, icon: Plus, always: true },
  { id: 'friends', path: '/friends', icon: Users, feature: 'friends' },
  { id: 'notifications', path: '/notifications', icon: Bell, feature: 'notifications' },
];

const yogaShortcuts = [
  { label: 'Yoga Poses', yocasType: 'Pose' },
  { label: 'Yoga Session', yocasType: 'session' },
  { label: 'Yoga Modifications', yocasType: 'modification' },
];

export function TabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [plusOpen, setPlusOpen] = useState(false);

  const visibleTabs = tabs.filter(
    (tab) => tab.always || (tab.feature && FEATURES[tab.feature])
  );

  const isActive = (tab) => {
    if (tab.id === 'feed') return location.pathname === '/';
    if (tab.id === 'plus') return false;
    return tab.path && location.pathname.startsWith(tab.path);
  };

  const handleTabClick = (tab) => {
    if (tab.id === 'plus') {
      setPlusOpen((open) => !open);
      return;
    }
    setPlusOpen(false);
    if (tab.path) navigate(tab.path);
  };

  const openVideos = (yocasType) => {
    setPlusOpen(false);
    navigate('/videos', { state: { yocasType } });
  };

  return (
    <>
      {plusOpen && FEATURES.videos && (
        <div
          className="fixed inset-x-0 z-50 flex max-h-[min(42vh,calc(100dvh-6rem))] flex-col-reverse items-stretch gap-2 overflow-y-auto px-4 pb-2 md:hidden"
          style={{ bottom: 'calc(72px + env(safe-area-inset-bottom))' }}
        >
          {yogaShortcuts.map((item) => (
            <button
              key={item.yocasType}
              type="button"
              onClick={() => openVideos(item.yocasType)}
              className="shrink-0 rounded-xl bg-nav px-4 py-3 text-center text-sm font-semibold text-accent-peach shadow-[var(--shadow-lg)] transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98]"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      <nav className="fixed bottom-0 left-0 right-0 z-40 rounded-t-[var(--radius-card)] bg-nav tab-bar-height shadow-[0_-4px_24px_rgba(58,61,98,0.2)] md:hidden">
        <div className="flex h-[72px] items-stretch justify-around px-2">
          {visibleTabs.map((tab) => {
            const active = isActive(tab);
            const isPlus = tab.id === 'plus';
            const Icon = isPlus && plusOpen ? X : tab.icon;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab)}
                className="flex flex-1 flex-col items-center justify-center gap-1 min-h-0 min-w-0"
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-200 ${
                    active ? 'bg-accent-peach/25' : isPlus && plusOpen ? 'bg-accent-peach/25' : ''
                  }`}
                >
                  <Icon
                    className={`h-6 w-6 transition-colors duration-200 ${
                      active || (isPlus && plusOpen) ? 'text-nav-icon' : 'text-nav-icon/70'
                    }`}
                    strokeWidth={active ? 2.5 : 2}
                  />
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
